import type React from 'react';
import {useState} from 'react';
import {generatePath, Link as RouterLink} from 'react-router';
import {Button, Divider, Paper, Stack, Typography} from '@mui/material';
import {Tag} from '~/components/tag/Tag';
import {routes} from '~/routing/routes';
import type {PlanProps} from '../strategyProps';
import {HelpTitle} from '../SectionLinks';
import {DecideDialog} from './DecideDialog';
import {SubmitDialog} from './SubmitDialog';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const STATUS = {
    draft: {label: 'Draft', color: 'default'},
    awaiting: {label: 'Awaiting sign-off', color: 'info'},
    approved: {label: 'Approved', color: 'success'},
    returned: {label: 'Returned with comments', color: 'warning'},
} as const;

const GATEWAY_HELP = 'Submit the plan when it is ready; it locks until it is signed off or withdrawn. The approvers are '
    + 'set by Mithra staff in the settings (sponsor, finance, steering committee, or a subset). They sign off on the '
    + 'Approval page; any of them can return the plan with a comment. Changing an approved plan reopens it as a draft.';

const STATE = {pending: {label: 'Pending', color: 'default'}, approved: {label: 'Approved', color: 'success'},
    returned: {label: 'Returned', color: 'warning'}} as const;

const EVENT = {
    submitted: 'submitted the plan', resubmitted: 'resubmitted the plan', withdrawn: 'withdrew the submission',
    approved: 'signed off', returned: 'returned the plan', plan_approved: 'Plan approved', reopened: 'reopened the plan',
} as Record<string, string>;

const WHEN = new Intl.DateTimeFormat(LOCALE, {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'});

/**
 * Gateway approval: status, approvers and their decisions, submit / withdraw / resubmit, activity. Approvers sign off on
 * the Approval page (`onApprovalPage`); the Strategy tab links there.
 */
export const GatewayCard: React.FC<Pick<PlanProps, 'plan' | 'actions'> & {onApprovalPage?: boolean}> = ({plan, actions, onApprovalPage = false}) => {
    const [dialog, setDialog] = useState<'submit' | 'approved' | 'returned' | null>(null);
    const status = STATUS[plan.status];
    const pending = plan.approvers.filter(a => a.state === 'pending');
    const returned = plan.approvers.find(a => a.state === 'returned');

    return (
        <Paper variant="outlined" sx={{p: 2}}>
            <Stack gap={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600} component="div">
                        <HelpTitle title="Gateway approval" help={GATEWAY_HELP} links={[]}/>
                    </Typography>
                    <Tag label={status.label} color={status.color}/>
                </Stack>
                {plan.submission && plan.status !== 'draft' && (
                    <Typography variant="caption" color="text.secondary">
                        {`Round ${plan.submission.round}, submitted by ${plan.submission.byMe ? 'you' : plan.submission.by}`}
                    </Typography>
                )}
                <Stack gap={1}>
                    {plan.approvers.map(a => (
                        <Stack key={a.slot} direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                            <div>
                                <Typography variant="body2">{a.label}</Typography>
                                {a.by && <Typography variant="caption" color="text.secondary">{a.by}</Typography>}
                            </div>
                            <Tag size="small" solid={false} label={STATE[plan.status === 'draft' ? 'pending' : a.state].label}
                                 color={STATE[plan.status === 'draft' ? 'pending' : a.state].color}/>
                        </Stack>
                    ))}
                </Stack>
                {plan.status === 'returned' && returned?.comment && (
                    <Typography variant="body2" sx={{bgcolor: 'action.hover', p: 1, borderRadius: 1, whiteSpace: 'pre-wrap'}}>
                        {`${returned.label}: ${returned.comment}`}
                    </Typography>
                )}

                {plan.canSubmit && !onApprovalPage && (
                    <Button variant="contained" onClick={() => setDialog('submit')}>
                        {plan.status === 'returned' ? 'Resubmit' : 'Submit for sign-off'}
                    </Button>
                )}
                {plan.status === 'awaiting' && plan.submission?.byMe && !onApprovalPage && (
                    <Button onClick={() => void actions.withdraw()}>Withdraw submission</Button>
                )}
                {onApprovalPage && plan.canApprove && pending.length > 0 && (
                    <Stack direction="row" gap={1}>
                        <Button variant="contained" sx={{flex: 1}} onClick={() => setDialog('approved')}>Sign off</Button>
                        <Button color="warning" sx={{flex: 1}} onClick={() => setDialog('returned')}>Return</Button>
                    </Stack>
                )}
                {!onApprovalPage && plan.status === 'awaiting' && plan.approvalRequestId && (
                    <Button variant={plan.canApprove ? 'contained' : 'outlined'} component={RouterLink}
                            to={generatePath(routes.approval_plan_detail, {approvalId: String(plan.approvalRequestId)})}>
                        {plan.canApprove ? 'Sign off on the Approval page' : 'View on the Approval page'}
                    </Button>
                )}
                {plan.status === 'awaiting' && !plan.canApprove && (
                    <Typography variant="caption" color="text.secondary">Super users sign off on the Approval page.</Typography>
                )}

                {plan.events.length > 0 && (
                    <>
                        <Divider/>
                        <Typography variant="subtitle2">Activity</Typography>
                        <Stack gap={0.75}>
                            {plan.events.slice(0, 6).map((e, i) => (
                                <Typography key={i} variant="caption" sx={{whiteSpace: 'pre-wrap'}}>
                                    <b>{e.kind === 'plan_approved' ? '' : `${e.actor} `}{EVENT[e.kind] ?? e.kind}</b>
                                    {` · ${WHEN.format(new Date(e.at))}`}
                                    {e.note && <><br/>{e.note}</>}
                                </Typography>
                            ))}
                        </Stack>
                    </>
                )}
            </Stack>

            {dialog === 'submit' && <SubmitDialog plan={plan} onSubmit={actions.submit} onClose={() => setDialog(null)}/>}
            {(dialog === 'approved' || dialog === 'returned') && (
                <DecideDialog pending={pending} decision={dialog}
                              onDecide={(slot, comment) => actions.decide(slot, dialog, comment)}
                              onClose={() => setDialog(null)}/>
            )}
        </Paper>
    );
};
